import { createFileRoute, redirect, notFound } from "@tanstack/react-router";
import { BLOG_MERGED_REDIRECTS } from "@/content/learn/slugs";
import { BlogPost, LearnTopic } from "@/lib/lazyPages";
import { seoHead } from "@/lib/seo/head";
import { getPublicCourses } from "@/lib/publicCourses.functions";
import { getFlywheelArticle } from "@/lib/flywheelArticles.functions";
import { getBlogCredit } from "@/lib/blogAuthors.functions";
import { DEFAULT_BLOG_COVER_WEBP, shareImage } from "@/lib/blogPublishing";
import { registeredBlogAuthor, DEFAULT_BLOG_AUTHOR } from "@/content/blog/authors";
import { storedPostMeta, storedTopicMeta } from "@/lib/storedArticle";


/** A course link only when the course exists and is published; otherwise null. */
async function publishedCourse(slug?: string): Promise<{ slug: string; title: string } | null> {
  if (!slug) return null;
  try {
    const c = (await getPublicCourses()).flatMap((l) => l.courses).find((x) => x.slug === slug);
    return c && c.status === "published" ? { slug: c.slug, title: c.title } : null;
  } catch {
    return null;
  }
}

// /blog/$slug serves both blog posts and the parent guides that used to live
// under /learn (those now 301 here). Blog posts win on slug; guide slugs that
// collided were given a distinct /blog slug in src/content/learn/slugs.ts.
export const Route = createFileRoute("/blog/$slug")({
  beforeLoad: ({ params }) => {
    const target = BLOG_MERGED_REDIRECTS[params.slug];
    if (target) {
      throw redirect({ to: "/blog/$slug", params: { slug: target }, replace: true, statusCode: 301 });
    }
  },
  // Dev-only verification switch: ?source=db renders unverified migrated rows, ?source=code forces the code copy.
  loaderDeps: ({ search }) => ({ source: import.meta.env.DEV ? (search as { source?: string }).source : undefined }),
  loader: async ({ params, deps }) => {
    const override = deps.source === "db" || deps.source === "code" ? deps.source : null;
    const db = override === "code" ? { article: null, owned: false } : await getFlywheelArticle({ data: { slug: params.slug, preferDb: override === "db" } });
    if (db.owned && !db.article) throw notFound();
    const credit = await getBlogCredit({ data: params.slug });
    const author = credit.author ? registeredBlogAuthor(credit.author) : DEFAULT_BLOG_AUTHOR;
    const cover = credit.cover ?? DEFAULT_BLOG_COVER_WEBP;
    const ogImage = shareImage(cover, credit.og);
    const [{ getPostBySlug }, { blogPostSchemas }] = await Promise.all([
      import("@/content/blog"),
      import("@/content/blog/schema"),
    ]);
    const stored = db.article && db.article.layout !== "flywheel" ? db.article : null;
    const post = stored?.layout === "post" ? { meta: storedPostMeta(stored) } : stored ? undefined : getPostBySlug(params.slug);
    if (post) {
      const m = post.meta;
      return {
        kind: "post" as const, owner: stored ? "flywheel" as const : "code-owned" as const,
        stored: stored ? { meta: m, body: stored.body, format: stored.body_format } : null,
        title: m.title,
        description: m.description,
        slug: m.slug,
        date: m.date,
        dateModified: m.dateModified ?? m.date,
        category: m.category,
        authorName: author.name,
        author, cover, ogImage,
        jsonLd: blogPostSchemas({ ...m, coverImage: ogImage, credit: { ...m.credit, author } }),
        course: await publishedCourse(m.course),
      };
    }
    const [{ learnTopicsByBlogSlug }, { learnTopicSchemas }] = await Promise.all([
      import("@/content/learn"),
      import("@/content/learn/schema"),
    ]);
    const storedTopic = stored?.layout === "guide" ? storedTopicMeta(stored) : null;
    const topic = storedTopic ?? (stored ? undefined : learnTopicsByBlogSlug[params.slug]);
    if (!topic) {
      const article = db.article;
      if (!article) throw notFound();
      const fwOg = shareImage(article.cover_image_url, article.og_image_url);
      const meta = {
        slug: article.slug, title: article.meta_title, description: article.meta_description,
        date: article.published_at ?? article.created_at, dateModified: article.updated_at,
        category: article.category, excerpt: article.meta_description, readMinutes: 1,
        author,
        coverImage: fwOg,
        faq: article.faq.map((f: { question: string; answer: string }) => ({ q: f.question, a: f.answer })),
      };
      const jsonLd = blogPostSchemas(meta);
      jsonLd[0] = { ...jsonLd[0], image: fwOg };
      return { kind: "flywheel" as const, owner: "flywheel" as const, article, title: article.meta_title,
        description: article.meta_description, slug: article.slug, date: meta.date,
        dateModified: article.updated_at, category: article.category, authorName: author.name, author, cover: article.cover_image_url, ogImage: fwOg,
        jsonLd, course: null };
    }
    return {
      kind: "guide" as const, owner: storedTopic ? "flywheel" as const : "code-owned" as const,
      stored: storedTopic && stored ? { topic: storedTopic, body: stored.body, format: stored.body_format } : null,
      title: topic.title,
      description: topic.description,
      author, cover, ogImage,
      slug: params.slug,
      jsonLd: learnTopicSchemas({
        slug: topic.slug,
        keyword: topic.keyword,
        title: topic.title,
        description: topic.description,
        updated: topic.updated,
        howTo: topic.howTo,
        faq: topic.faq,
        author, coverImage: ogImage,
      }),
    };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return seoHead({ title: "Article not found", noindex: true });
    const h = articleHead(loaderData);
    return { ...h, meta: [...h.meta, { name: "flywheel-article-owner", content: loaderData.owner }] };
  },
  component: BlogSlugPage,
});

interface HeadInput {
  kind: "post" | "guide" | "flywheel"; title: string; description: string; slug: string; cover: string; ogImage: string;
  jsonLd: Record<string, unknown>[]; date?: string; dateModified?: string; authorName?: string; category?: string;
}
function articleHead(loaderData: HeadInput): ReturnType<typeof seoHead> {
    if (loaderData.kind === "guide") {
      return seoHead({
        title: loaderData.title,
        description: loaderData.description,
        canonical: `/blog/${loaderData.slug}`,
        ogType: "article",
        ogImage: loaderData.ogImage,
        jsonLd: loaderData.jsonLd,
      });
    }
    return seoHead({
      title: loaderData.title,
      description: loaderData.description,
      canonical: `/blog/${loaderData.slug}`,
      ogType: "article",
      ogImage: loaderData.ogImage,
      article: {
        publishedTime: loaderData.date,
        modifiedTime: loaderData.dateModified,
        authorName: loaderData.authorName,
        section: loaderData.category,
      },
      jsonLd: loaderData.jsonLd,
    });
}

function BlogSlugPage() {
  const data = Route.useLoaderData();
  return data?.kind === "guide" ? <LearnTopic author={data.author} cover={data.cover} stored={data.stored ?? undefined} /> : <BlogPost stored={data?.kind === "post" ? data.stored ?? undefined : undefined} author={data?.author} cover={data?.cover} course={data?.course ?? null} flywheel={data?.kind === "flywheel" ? data.article : undefined} />;
}
